import Link from "next/link";
import React, { useCallback, useState } from "react";
import { Button, Skeleton, Typography } from "antd";
import MyCard from "@/app/components/MyCard";
import { IMAGE_CDN_BLOG } from "@/constant/apiPath";
import type { BlogData, INewsResponse } from "@/models/news";
import Image from "next/image";
import { Input, notification } from "antd/lib";
import { useTranslations } from "next-intl";
import { MailFilled } from "@ant-design/icons";
import { validateEmail } from "@/helpers";
import { axiosClient } from "@/lib/axiosClient";
import { useLanguageContext } from "@/context/LanguageContext";
import AdUnit from "../AdUnit";
import ExchangeCard from "./ExchangeCard";
import TokenList from "./TokenList";

interface DetailsSidebarProps {
  blogList?: BlogData[] | INewsResponse[];
}

const DetailsSidebar: React.FC<DetailsSidebarProps> = ({ blogList }) => (
  <div className="grid-area-moreForYou h-fit flex flex-col gap-4 sticky md:top-32">
    <ExchangeCard />
    <MoreForYou data={blogList} />
    <TokenList title="Top Gainers" url="/v1/market-movement/token-info/public/gainers" />
    <TokenList title="Top Losers" url="/v1/market-movement/token-info/public/losers" />
    <TokenList title="Most Active" url="/v1/market-movement/token-info/public/trending" />
    <NewsletterSubscription />
    {/* <AdUnit
      slot="3458901882"
      layout="in-article"
      style={{
        display: "block",
        textAlign: "center",
      }}
    /> */}
    {/* <AdsSidebarBanner /> */}
  </div>
);

const MoreForYou: React.FC<{ data?: BlogData[] | INewsResponse[] }> = ({ data }) => {
  const shuffledData = data?.sort(() => Math.random() - 0.5).slice(0, 5);
  return (
    <MyCard title="More for You" className="w-full border-[#e5e7eb] border-solid border rounded-lg p-5 pb-0 shadow-sm">
      <div className="flex flex-col space-y-2">
        {shuffledData?.map(item => (
          <Link href={`/news/blogs/${item.url}`} key={item.id}>
            <NewShortSummaryItem news={item} />
          </Link>
        ))}
      </div>
    </MyCard>
  );
};

const NewsletterSubscription: React.FC = () => {
  const t = useTranslations("news");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [email, setEmail] = useState<string>("");

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsEmailValid(validateEmail(newEmail));
  };

  const handleSubscribe = useCallback(() => {
    if (!validateEmail(email)) {
      notification.error({
        message: "Invalid Email",
        description: "Please enter a valid email address.",
      });
      return;
    }

    axiosClient
      .post("/v1/user/submit-klarda", { email })
      .then(res => {
        notification.success({
          message: "Subscription Successful",
          description: "You have successfully subscribed!",
        });
        console.log(res);
      })
      .catch(error => {
        notification.error({
          message: "Subscription Failed",
          description: error.response?.data?.message || "Something went wrong. Please try again.",
        });
        console.error(error);
      });
  }, [email]);

  return (
    <div className="w-full border border-[#e5e7eb] rounded-lg p-5">
      <h2 className="text-sm font-bold mb-3">{t("Get_the_box")}</h2>
      <p className="text-sm text-gray-500 mb-4">{t("Subscribe_our")}</p>
      <Input
        onChange={handleEmailChange}
        type="email"
        placeholder="Your email"
        prefix={<MailFilled />}
        className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
        status={!isEmailValid ? "error" : ""}
      />
      <Button
        size="large"
        type="primary"
        className="w-full rounded-md shadow-sm"
        disabled={!email || !isEmailValid}
        onClick={handleSubscribe}
      >
        {t("Subscribe")}
      </Button>
    </div>
  );
};

// const AdsSidebarBanner: React.FC = () => {
//   const t = useTranslations("news");
//   return (
//     <div className="w-full border border-[#e5e7eb] rounded-lg p-5">
//       <Image src={imageAdsAdobe} alt={"ads-adobe"} width={300} height={250} />
//       <div className="flex flex-col gap-1">
//         <Typography.Text type="secondary">{t("Students")}</Typography.Text>
//         <Typography.Text type="secondary">{t("Advertising")}</Typography.Text>
//       </div>
//     </div>
//   );
// };

const NewShortSummaryItem: React.FC<{ news?: BlogData | INewsResponse }> = ({ news }) => {
  const { language } = useLanguageContext();
  return (
    <div className="flex gap-2">
      <div className="w-1/4">
        <div className="relative min-h-full w-full overflow-hidden rounded hover:p-1">
          {!news ? (
            <Skeleton.Image className="h-full w-full" active />
          ) : (
            <Link
              href={
                "is_klarda" in news && !news.is_klarda
                  ? `/${language}/news/${news.id}`
                  : `/${language}/news/blogs/${news.url}`
              }
              passHref
            >
              <Image
                src={`${IMAGE_CDN_BLOG}${
                  typeof news.thumbnail === "object" ? news.thumbnail.imageKey : news.thumbnail
                }`}
                alt={typeof news.thumbnail === "object" ? news.thumbnail.alt : "Klarda Blog"}
                style={{ objectFit: "fill", objectPosition: "0px 0%" }}
                title={typeof news.thumbnail === "object" ? news.thumbnail.description : "Klarda Blog"}
                width={100}
                height={100}
              />
            </Link>
          )}
        </div>
      </div>
      <div className="flex-1">
        <Skeleton active loading={!news} paragraph={false}>
          {news && (
            <Link
              href={
                "is_klarda" in news && !news.is_klarda
                  ? `/${language}/news/${news.id}`
                  : `/${language}/news/blogs/${news.url}`
              }
              passHref
            >
              <Typography.Paragraph strong ellipsis={{ rows: 3 }}>
                {news.title}
              </Typography.Paragraph>
            </Link>
          )}
        </Skeleton>
      </div>
    </div>
  );
};

export default DetailsSidebar;
